import { z } from "zod/v4";
import { dimensionSchema, type QuoteDimension, type QuantityDerivation } from "./quantity-dimensions";
import { KEY_MEASUREMENT_CHECKLIST_PROMPT } from "./measurement-checklist";
export { keyMeasurementGroup, KEY_MEASUREMENT_CHECKLIST_PROMPT } from "./measurement-checklist";
export { CONSTRUCTION_GEOMETRY_PROMPT, isConstructionRun } from "./construction-geometry";

export const measurementSubjectSchema = z.object({
  id: z.string().min(1).max(80),
  label: z.string().min(1).max(180),
  kind: z.enum(["opening", "chimney", "extension", "loft", "roof", "room", "wall", "groundworks", "external", "steel", "floor"]),
  beamSize: z.string().trim().min(1).max(100).optional(),
  dimensionEvidenceIds: z.array(z.string().min(1).max(100)).max(20).default([]),
});
export type MeasurementSubject = z.infer<typeof measurementSubjectSchema>;

export function parseMeasurementSubjects(raw: unknown, evidence: QuoteDimension[]): MeasurementSubject[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const known = new Set(evidence.map(e => e.id));
  return raw.slice(0, 25).flatMap(value => {
    const parsed = measurementSubjectSchema.safeParse(value);
    if (!parsed.success || seen.has(parsed.data.id)) return [];
    seen.add(parsed.data.id);
    return [{ ...parsed.data, dimensionEvidenceIds: [...new Set(parsed.data.dimensionEvidenceIds)].filter(id => known.has(id)) }];
  });
}

export const MEASUREMENT_SUBJECTS_PROMPT = `
Also return "measurementSubjects": [{"id":"opening-1","label":"New kitchen/dining opening","kind":"opening","dimensionEvidenceIds":["e1"]}].
This is a builder's optional pre-pricing measurement review, not an engineering schedule.
Identify EVERY distinct main physical work area from the original scope AND drawing layout: separate openings/doorways/spans, chimney work, extensions, roofs, rooms, walls or groundworks. Allowed kinds: opening, chimney, extension, loft, roof, room, wall, groundworks, external, steel, floor.
One subject per distinct opening/location, even where the width/height is not stated. Name each by its actual location/reference so multiple openings remain separate. For an opening with an identified supporting steel, also return "beamSize":"178 × 102 × 19 UB" using its FULL exact section designation from the original drawing/scope, not just "178 UB". This is a recognition label, not an opening dimension; omit beamSize when unknown. Inspect the layout, not just its dimension callouts: an undimensioned opening must not disappear. Existing-opening alterations/replacement spans are not automatically new demolition openings; describe their actual work. Cross-beams, connections, sections and duplicate drawing views do NOT create extra openings.
Include chimney work as its own subject when in scope; support/retention alone is NOT evidence of removing the whole chimney. Subject labels and dimensions never add work to the described scope.
Give dimensionEvidence entries unique consecutive IDs e1, e2, ... in ARRAY ORDER; link every available width/height/length to its particular subject using those exact IDs. Always retain legible numeric opening callouts, including stated maximum widths, rather than returning empty links when a number is on the drawing. Include the subject's location/reference in each evidence label/note. Keep genuinely missing dimensions absent, with [] only when none are known. A maximum permitted width is a design limit: retain that number as labelled maximum evidence linked to its opening, never invent a different actual width from it.
FOOTPRINT COVERAGE CHECK: For each proposed extension, inspect BOTH perpendicular overall plan dimension lines, including rotated/vertical callouts outside the walls. Explicitly capture the across-building width AND front-to-back length/depth/projection when dimensioned; link BOTH to that extension subject. Convert millimetres to metres without rounding away precision. A callout need not appear in the description prose to belong in dimensionEvidence. Before returning, compare the extension's linked evidence against the proposed/GA plan so a legible overall dimension is not left blank. Retain a source sheet reference and whether each dimension is external overall or internal clear. Do not substitute a utility/shower room size, roof-light size, foundation depth or existing building dimension for the extension footprint; do not copy external footprint sizes into internal rooms or roof measurements unless their own drawing evidence establishes that size. Use explicit length/width labels for the footprint (depth/projection means the front-to-back plan length, not a third axis). Keep genuinely undimensioned sizes absent rather than scaling or guessing a measured value.
Retain the original technical evidence, but do NOT create separate measurement subjects for padstones, bearings, member cross-sections, plates, bolts or fixings; bearing lengths belong to their steel member. Keep supporting trades such as local plaster making-good inside their parent job, not as whole-room survey subjects. Empty subject list is valid when no applicable work area can be identified.
${KEY_MEASUREMENT_CHECKLIST_PROMPT}
`;

/** Explicit builder input, separate from AI evidence. Skipping never confirms it. */
export const measurementReviewSchema = z.object({
  measurements: z.array(dimensionSchema).max(150),
  omittedEvidenceIds: z.array(z.string().min(1).max(100)).max(150).default([]),
}).superRefine((review, ctx) => {
  if (new Set(review.measurements.map(d => d.id)).size !== review.measurements.length)
    ctx.addIssue({ code: "custom", message: "Measurement IDs must be unique" });
  review.measurements.forEach((d, i) => {
    if (d.source !== "entered")
      ctx.addIssue({ code: "custom", path: ["measurements", i, "source"], message: "Reviewed measurements must be builder entries" });
    if (review.omittedEvidenceIds.includes(d.id))
      ctx.addIssue({ code: "custom", message: "A measurement cannot be both used and omitted" });
  });
});
export type MeasurementReview = z.infer<typeof measurementReviewSchema>;

export function applyMeasurementReview(evidence: QuoteDimension[], review?: MeasurementReview): QuoteDimension[] {
  if (!review) return evidence;
  const parsed = measurementReviewSchema.parse(review);
  const replacements = new Set([...parsed.omittedEvidenceIds, ...parsed.measurements.map(d => d.id)]);
  return [
    ...evidence.filter(d => !replacements.has(d.id)),
    ...parsed.measurements.map(d => ({ ...d, source: "entered" as const,
      note: `Builder reviewed before quote generation. ${d.note}`.slice(0, 1000) })),
  ];
}

/** Keep variant-linked IDs while overriding only an unambiguous, named measure. */
export function combineReviewedEvidence(aiEvidence: QuoteDimension[], contextEvidence: QuoteDimension[], preserveReviewedIds = false): QuoteDimension[] {
  const norm = (d: QuoteDimension) => `${d.label.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim()}:${d.unit}`;
  const measureAxis = (label: string) => label.toLowerCase().trim().match(/\b(width|height|length|depth|projection|area|volume|weight|count)\s*$/)?.[1];
  if (preserveReviewedIds) {
    // With no variant-owned references, keep the builder's original IDs while
    // retaining every other drawing dimension. Rename collisions, not evidence.
    const occupied = new Set(contextEvidence.map(e => e.id));
    const used = new Set<QuoteDimension>();
    const claim = (id: string) => {
      let candidate = id, suffix = 0;
      while (occupied.has(candidate)) candidate = `drawing-${++suffix}-${id.slice(0, 80)}`;
      occupied.add(candidate);
      return candidate;
    };
    const merged = aiEvidence.map(d => {
      const linked = contextEvidence.filter(e => e.source === "entered" && e.unit === d.unit && e.id === d.evidenceId &&
        measureAxis(d.label) != null && measureAxis(d.label) === measureAxis(e.label));
      const matches = linked.length ? linked : aiEvidence.filter(e => norm(e) === norm(d)).length === 1
        ? contextEvidence.filter(e => e.source === "entered" && norm(e) === norm(d)) : [];
      if (matches.length === 1 && !used.has(matches[0])) {
        used.add(matches[0]);
        return { ...matches[0] };
      }
      return { ...d, id: claim(d.id) };
    });
    return [...merged, ...contextEvidence.filter(e => !used.has(e) &&
      (e.source === "entered" || !merged.some(d => norm(d) === norm(e))))];
  }
  const used = new Set<QuoteDimension>();
  const merged = aiEvidence.map(d => {
    const axis = (label: string) => label.toLowerCase().match(/\b(width|height|length|depth|projection|area|volume|weight|count)\s*$/)?.[1];
    const matches = contextEvidence.filter(e => e.source === "entered" && e.unit === d.unit &&
      (norm(e) === norm(d) || (d.evidenceId === e.id && axis(d.label) != null && axis(d.label) === axis(e.label))));
    const aiPeers = aiEvidence.filter(e => norm(e) === norm(d));
    if (matches.length !== 1 || aiPeers.length !== 1) return d;
    used.add(matches[0]);
    return { ...matches[0], id: d.id };
  });
  return [...merged, ...contextEvidence.filter(e => !used.has(e)).map(e => ({ ...e, id: `context-${e.id}` }))];
}

/** Provenance only: never rewrite an AI quantity or claim a mismatching value was used. */
export function retainReviewedDimensionSources(data: QuantityDerivation, evidence: QuoteDimension[]): QuantityDerivation {
  const reviewed = evidence.filter(d => d.source === "entered");
  const norm = (label: string) => label.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  return { ...data, dimensions: data.dimensions.map(d => {
    const linked = d.evidenceId ? reviewed.filter(e => e.id === d.evidenceId) :
      reviewed.filter(e => norm(e.label) === norm(d.label));
    if (linked.length !== 1 || linked[0].unit !== d.unit || linked[0].value !== d.value) return d;
    const e = linked[0];
    return { ...d, source: "entered" as const, entryOrigin: e.entryOrigin,
      evidenceId: e.id, note: e.note,
      originalValue: e.originalValue ?? e.value, originalSource: e.originalSource ?? "entered" as const };
  }) };
}

/** Apply a reviewed value only to a uniquely identified same measurement.
 * In particular, Opening width and Beam length are never interchangeable.
 * Unlike the provenance-only helper, this changes the value actually used. */
export function applyReviewedDimensionValues(data: QuantityDerivation, evidence: QuoteDimension[]): QuantityDerivation {
  const norm = (label: string) => label.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  const axis = (label: string) => norm(label).match(/\b(width|height|length|depth|projection|area|volume|weight|count)$/)?.[1];
  const reviewed = evidence.filter(d => d.source === "entered");
  return { ...data, dimensions: data.dimensions.map(d => {
    const linked = reviewed.filter(e => e.unit === d.unit &&
      (norm(e.label) === norm(d.label) || (e.id === d.evidenceId && axis(e.label) != null && axis(e.label) === axis(d.label))));
    if (linked.length !== 1) return d;
    const e = linked[0];
    // Never multiply one matching source into ambiguous same-label instances.
    if (data.dimensions.filter(other => norm(other.label) === norm(d.label)).length !== 1) return d;
    return { ...d, value: e.value, source: "entered" as const, evidenceId: e.id, entryOrigin: e.entryOrigin,
      note: e.note, originalValue: e.originalValue ?? d.originalValue ?? d.value,
      originalSource: e.originalSource ?? d.originalSource ?? d.source };
  }) };
}

export const REVIEWED_MEASUREMENT_PROMPT = `
BUILDER-REVIEWED MEASUREMENTS:
Dimension evidence with source "entered" was explicitly confirmed, adjusted or
supplied by the builder before this generation. Use its current value and unit
for the named scope/work item in preference to conflicting original drawings,
AI readings, assumptions, generated prose or originalValue. originalValue is
historical provenance, NOT an alternative measurement to price.
Apply a dimension only to its named room, element or scope. Never spread a
room-local measurement across the whole property or unrelated scopes.
Preserve explicit openings/net-area exclusions when deriving quantities.
Do not invent dimensions for blank/omitted optional fields, add scope merely
because a measurement is supplied, or require measurements to generate a quote.
Retain used measurement evidence IDs and values in quantityDerivation so the
builder can see the dimensions actually used. Missing evidence remains assumed.
Keep opening clear widths distinct from full beam cut lengths, which include
bearings and may remain assumed. A doorway below a beam does not establish that
beam's full span. Attach each reviewed opening width to the actual opening
formation/alteration work at that location, even when the work is priced each;
do not omit it simply because another beam has the same section designation.
`;
